import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import initBlogBehavior from './blog/blogBehavior';
import { getCachedBlogPage, loadBlogPage, primeBlogPage } from './blog/blogCache';
import { BLOG_STYLE_HREF, LANDING_STYLE_LIST } from './landingStyles';

// Blog sayfası (PublicSite içinde, ana sayfayla aynı yapıda). İçerik derlemede hazırlanır
// (scripts/build-blog.mjs): /blog-data/index.json ve /blog-data/<slug>.json. Doğrudan açılan /blog/...
// adreslerinde statik sayfa aynı veriyi <script id="tivigo-blog-data"> içinde taşır; o varsa ağ isteği
// yapılmaz. Bir kez alınan sayfa önbellekte tutulur. Sayfa HTML'i olduğu gibi basılır, menüdeki
// bağlantılar React yönlendirmesiyle çalışır (sayfa yenilenmez).
const LANDING_STYLES = [...LANDING_STYLE_LIST, BLOG_STYLE_HREF];

const readEmbedded = (slug) => {
  const node = document.getElementById('tivigo-blog-data');
  if (!node) return null;
  try {
    const page = JSON.parse(node.textContent);
    return page && (page.slug || '') === slug ? page : null;
  } catch {
    return null;
  }
};

const setMeta = (attribute, key, content) => {
  let meta = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, key);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

const applyPageSeo = (page) => {
  document.title = page.title;
  setMeta('name', 'description', page.description);
  setMeta('name', 'robots', 'index, follow, max-image-preview:large, max-snippet:-1');
  setMeta('name', 'googlebot', 'index, follow, max-image-preview:large, max-snippet:-1');
  setMeta('property', 'og:type', page.ogType);
  setMeta('property', 'og:title', page.title);
  setMeta('property', 'og:description', page.description);
  setMeta('property', 'og:url', page.canonical);
  setMeta('property', 'og:image', page.ogImage);
  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', page.title);
  setMeta('name', 'twitter:description', page.description);
  setMeta('name', 'twitter:image', page.ogImage);
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', page.canonical);
  document.head.querySelectorAll('script[data-blog-jsonld]').forEach((node) => node.remove());
  const jsonLd = document.createElement('script');
  jsonLd.type = 'application/ld+json';
  jsonLd.dataset.blogJsonld = 'true';
  jsonLd.textContent = JSON.stringify(page.jsonLd);
  document.head.appendChild(jsonLd);
};

export default function BlogPage({ slug = '' }) {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [page, setPage] = useState(() => readEmbedded(slug) || getCachedBlogPage(slug));
  const [error, setError] = useState('');

  // Stiller yüklenmeden içerik gösterilmez (stilsiz bir an görünmesin).
  const [stylesReady, setStylesReady] = useState(false);

  // Ana sayfanın CSS'i ve blog stili yalnız bu sayfadayken yüklü kalır; panel sayfalarına sızmaz.
  // Ana sayfa canlı tutuluyorsa onun stil bağlantıları zaten yüklüdür, yenisi eklenmez.
  useLayoutEffect(() => {
    document.body.classList.add('premium-landing-page', 'blog-body');
    // Koyu zemin: geçişlerde sayfa zemini beyaza dönmesin (public/index.html'deki kural). Kaydırma çubuğu rengine dokunulmaz.
    document.documentElement.classList.add('tivigo-dark');
    const previousBackground = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#080a0f';
    let alive = true;
    const links = LANDING_STYLES.map((href) => {
      const existing = document.querySelector(`link[rel="stylesheet"][href="${href}"]`);
      if (existing) return existing;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.dataset.landingStyle = 'true';
      document.head.appendChild(link);
      return link;
    });
    const check = () => {
      if (alive && links.every((link) => link.sheet || link.dataset.failed)) setStylesReady(true);
    };
    links.forEach((link) => {
      if (link.sheet) return;
      link.addEventListener('load', check, { once: true });
      link.addEventListener('error', () => { link.dataset.failed = 'true'; check(); }, { once: true });
    });
    check();
    return () => {
      alive = false;
      document.body.classList.remove('premium-landing-page', 'blog-body', 'menu-open');
      document.documentElement.classList.remove('tivigo-dark');
      document.body.style.backgroundColor = previousBackground;
      // Bu sayfanın (ve statik sayfadan gelen) stil bağlantıları kaldırılır; ana sayfanınkilere dokunulmaz.
      document.head.querySelectorAll('link[data-landing-style]').forEach((link) => link.remove());
      document.head.querySelectorAll('script[data-blog-jsonld]').forEach((node) => node.remove());
    };
  }, []);

  useEffect(() => {
    const embedded = readEmbedded(slug);
    if (embedded) {
      primeBlogPage(embedded);
      setPage(embedded);
      setError('');
      return undefined;
    }
    const cached = getCachedBlogPage(slug);
    if (cached) {
      setPage(cached);
      setError('');
      return undefined;
    }
    let alive = true;
    setPage(null);
    setError('');
    loadBlogPage(slug)
      .then((data) => { if (alive) setPage(data); })
      .catch((caught) => { if (alive) setError(caught.message || 'Blog şu anda yüklenemiyor.'); });
    return () => { alive = false; };
  }, [slug]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!page || !root) return undefined;
    root.innerHTML = page.html;
    // Doğrudan açılan statik sayfada hazır içerik #root'un dışındadır ve React'in çizeceğiyle aynıdır. Önce üstte sabit
    // bir katman olarak kalır (görünüm değişmez; tıklamalar altındaki React içeriğine geçer). Tarayıcı yeni içeriği
    // boyayıp rasterlaştırana kadar (yavaş cihazda yüzlerce ms sürebilir) bekleyip sonra kaldırılır: arada boş an olmaz.
    const prerender = document.getElementById('blog-prerender');
    if (prerender && !root.contains(prerender)) {
      prerender.style.cssText = 'position:absolute;top:0;left:0;right:0;z-index:30;pointer-events:none';
      prerender.setAttribute('aria-hidden', 'true');
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => window.setTimeout(() => prerender.remove(), 600)));
    }
    if (!window.location.hash) window.scrollTo(0, 0);
    const cleanupBehavior = initBlogBehavior(root);
    const handleClick = (event) => {
      const link = event.target.closest('a[href]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const href = link.getAttribute('href') || '';
      if (!href.startsWith('/') || href.startsWith('//') || /\.(xml|html|pdf|zip|exe|apk|dmg)$/i.test(href) || link.hasAttribute('download')) return;
      event.preventDefault();
      navigate(href);
    };
    root.addEventListener('click', handleClick);
    return () => {
      root.removeEventListener('click', handleClick);
      cleanupBehavior();
      root.innerHTML = '';
    };
  }, [page, navigate]);

  useEffect(() => {
    if (page) applyPageSeo(page);
  }, [page]);

  if (error) {
    return (
      <main className="blog-main blog-status">
        <div className="container blog-document blog-status-card">
          <h1>{error}</h1>
          <p>Diğer yazılara blog sayfasından ulaşabilirsiniz.</p>
          <button type="button" className="button button-small button-primary" onClick={() => navigate('/blog/')}>Bloga dön</button>
        </div>
      </main>
    );
  }

  // Kaydırma çubuğu yer değiştirmesin diye kapsayıcı hep tam boy; içerik stiller yüklenene kadar gizli.
  return (
    <div
      ref={rootRef}
      className="blog-root"
      aria-busy={!page || !stylesReady}
      style={{ minHeight: '100vh', background: '#080a0f', visibility: page && stylesReady ? 'visible' : 'hidden' }}
    />
  );
}
